import type { MentorContext } from '../types';

/**
 * The one route in this product that leaves the tab to ask a model something.
 *
 * Modelled on `lib/verification.ts`, which is the other Worker-backed thing
 * the level screen draws, and deliberately so: same `credentials:
 * 'same-origin'`, same `cache: 'no-store'`, same abort signal, same
 * `{ used, limit, resetAt }` window shape read back into the rail. A second
 * pattern for the same job would be a second set of failure modes to get
 * right.
 *
 * ## Every failure is a value
 *
 * That is the one place this departs from `verification.ts`, which throws a
 * status-carrying `Error` and lets the panel branch on the number. Here there
 * is nothing underneath to catch a throw: the deterministic mentor that used
 * to answer every refused turn has been retired, so what a learner gets when
 * a turn produces nothing is a sentence naming the fact that stopped it, and
 * a branch forgotten inside a try/catch would be a spinner that stopped. So
 * there is no rejection path at all — a refusal, a quota, a dead network and
 * a body that is not JSON all come back as an `outcome`, and the component is
 * a switch over a closed set rather than a try/catch with a guess in it.
 *
 * ## What is sent
 *
 * The bounded context (`buildMentorContext`, in the engine), the mission id,
 * the language, and one of: nothing, the learner's own words, or which choice
 * they picked. The context carries the learner's work, because a mentor that
 * cannot see it can only restate the requirement: a grade that is current,
 * with what each failing check printed; the files the failing checks and git
 * point at, capped; and the terminal's newest lines. Every part of it has a
 * ceiling in the engine. No account and no id, and never anything at all on a
 * checkpoint — the Worker refuses those, and this never sends one.
 *
 * ## And now the conversation, which is the tab's and only the tab's
 *
 * A turn used to be the whole of what the model knew. It now carries a
 * `conversationId` this file mints and the exchanges so far, oldest first,
 * because an answer to "what did you try?" is worth nothing to a model that
 * cannot see the question it asked.
 *
 * **The transcript is state and not storage.** It lives in React, it is
 * posted on each turn, and it is never written to `localStorage`,
 * `sessionStorage`, IndexedDB or anywhere else — which is the promise
 * docs/mentor.md makes about typed text ("No message text and no reply text
 * is stored anywhere") and the reason a reload starts a new conversation
 * rather than resuming one. That is a property to state to the learner, not a
 * fault to apologise for.
 *
 * ## A conversation is finite, and the Worker is the one that says by how much
 *
 * Two limits bound it: a message cap on the conversation, and a disclosure
 * budget on the level for the day. **Both numbers arrive on the wire and
 * neither is declared here.** They were declared here, for about a day, and
 * the way that was wrong is worth keeping written down: the budget is counted
 * over `(user, mission, rolling day)` rather than over the conversation,
 * because the tab mints the conversation id and stores nothing — so a reload
 * minted a new id and would have bought a fresh budget. A sum this end could
 * compute was a sum that restarted at zero while the real one carried on.
 *
 * So the bar is drawn from `conversation.spent / budget` and
 * `messages / cap` as the Worker last reported them, the panel closes on the
 * numbers it was handed rather than on numbers it kept, and an unknown
 * `reason` still reads as "nothing came back" rather than as a guess about
 * which limit it was.
 */

/** One allowance window. The same three fields `verificationAllowance` returns. */
export interface MentorWindow {
  used: number;
  limit: number;
  /** When the window reopens, or null while it has not closed. */
  resetAt: number | null;
}

/**
 * What an account has left, across the three windows a turn is charged
 * against (docs/mentor-plan.md §5.6).
 *
 * `nextEligibleAt` is the Worker's own answer to "when does this stop
 * refusing" — the earliest reset of whichever windows are actually spent — so
 * the tab never has to work out which of three limits said no.
 */
export interface MentorAllowance {
  level: MentorWindow;
  day: MentorWindow;
  month: MentorWindow;
  /** The deployment is closed to new turns. Nothing the learner spent. */
  deploymentPaused: boolean;
  /**
   * This account has spent the whole of the course's mentor budget.
   *
   * One bit, where the Worker holds a number: what a learner has cost is an
   * operational figure and stays server-side, for the same reason the
   * deployment's own spend does — and for a stronger one, because a meter
   * reading "$3.12 of $5" over a conversation is a meter somebody starts
   * writing longer questions against, which is the exact failure the
   * disclosure bar is drawn without ticks to avoid.
   */
  budgetSpent: boolean;
  nextEligibleAt: number | null;
}

/**
 * The model's answer, as the Worker's schema pins it (§5.5).
 *
 * `look` is machine text — a command shape with `<placeholders>` — and stays
 * left-to-right in both languages, exactly as a cue's `focus` does.
 * `disclosure` is the ladder's own scale and never reaches five: rung five is
 * where the answer lives, and nothing here is allowed to go there.
 */
export interface MentorReply {
  say: string;
  look?: string;
  ask?: string;
  disclosure: 1 | 2 | 3 | 4;
  /** Which situation the model believes this is, by id, or none. */
  situation?: string;
}

/** `free` is signed in without the course; `paid` owns it. The Worker decides, never the tab. */
export type MentorTier = 'free' | 'paid';

/**
 * What came of one turn.
 *
 * **Thirteen outcomes**, and no two of them are the same fact about this turn.
 * It was eight before the conversation, which added `spent`, `capped`,
 * `not-yours` and a `refused` that had nowhere to be said. The count is
 * written out for `askDetail`'s reason, one paragraph down: a word that has
 * no home on the far side of a wire is discarded silently, so both ends say
 * how many they think there are.
 *
 * The two that are not failures are told apart because the funnel counts them
 * separately — an answer somebody else's identical state already paid for is
 * a different fact about this feature's cost from an answer this account
 * bought. The ten that are failures are told apart because the sentence the
 * learner reads is different for each, and it has to be: "you have used
 * today's questions", "that one was not answered" and "this level has had as
 * much said about it as a day allows" are three different things to do about,
 * and there is no free cue underneath any more to make the distinction
 * academic.
 */
export type MentorAsk =
  | { outcome: 'answered' | 'cached'; reply: MentorReply; allowance: MentorAllowance | null; tier: MentorTier; conversation: MentorSpend | null }
  /**
   * `filtered`: the output filter held the reply back — a reply existed and
   * was not allowed through. `refused`: the model declined the question, so
   * there was nothing written to hold back. One variant, two words, because
   * they carry the same fields and differ only in the sentence.
   */
  | { outcome: 'filtered' | 'refused'; allowance: MentorAllowance | null; tier: MentorTier; conversation: MentorSpend | null }
  /**
   * That is as far as this goes, and which limit says so.
   *
   * `spent` is the level's disclosure budget for today, and it arrives two
   * ways on purpose: a **429** when the level was out of points before the
   * message, and a **200 with `reason: 'spent'`** when the model answered
   * further up the ladder than there were points left for and the filter held
   * it. Both end the conversation; only the first has a `resetAt`, which is
   * why the sentence branches on the number rather than on the path.
   *
   * `capped` is this conversation's own message cap, which no wait returns.
   *
   * `tier` is nullable here and nowhere else: the 429 refuses before a turn is
   * reserved and does not say which tier refused, so the panel must not read
   * a missing field as `free` and tell a paying learner about the free one.
   */
  | { outcome: 'spent' | 'capped'; allowance: MentorAllowance | null; tier: MentorTier | null; conversation: MentorSpend | null }
  /** 401. Registration is the door, and there are no anonymous turns. */
  | { outcome: 'sign-in' }
  /**
   * 403: switched off here, a checkpoint, a level this tier is not playing —
   * or a tier with no mentor at all, which is what the free tier is while
   * `MENTOR_FREE_BUDGET` is zero.
   *
   * `tier` rides along and is the only thing that tells those apart from out
   * here, deliberately so: the Worker will not say WHICH of the four refused,
   * because three of them are facts about a level somebody could otherwise
   * probe for. The tier is a fact about the account, the same on every level,
   * and already readable from /api/entitlement. What it buys is one true
   * sentence instead of one misleading one — "this comes with the course"
   * rather than "this is not switched on here", to somebody for whom the
   * mentor is very much switched on and simply not theirs yet.
   */
  | { outcome: 'not-here'; tier: MentorTier | null }
  /**
   * 403: this conversation id belongs to another account, or to this account
   * on another level.
   *
   * Unreachable through the interface — it means an id that went stale or was
   * forged — so it is not an error to apologise for and not a dead panel. A
   * fresh id and an open composer is the answer, and the transcript goes with
   * the id because the Worker has just said it is not this conversation's.
   */
  | { outcome: 'not-yours' }
  /** 429. `allowance` says when it returns, and whether it was this account's to spend. */
  | { outcome: 'quota'; allowance: MentorAllowance | null }
  /**
   * 429, and the only refusal here that never comes back.
   *
   * The account has spent the whole of the course's mentor budget. It is not
   * `quota`, which is a sentence about waiting, and the two must not share a
   * word: `nextEligibleAt` is null for this one because there is no next time,
   * and a panel that read it as "no reset yet" would tell somebody to come
   * back tomorrow forever.
   */
  | { outcome: 'exhausted'; allowance: MentorAllowance | null }
  /** 409: a turn for this account is already in flight, in this tab or another. */
  | { outcome: 'busy' }
  /** Anything else: the network, a turn lost at the far end, a body that is not JSON. */
  | { outcome: 'error' };

/**
 * The word this outcome is counted under.
 *
 * `worker/events.ts` holds a closed list of **ten** for `mentor-ask`, and this
 * is the only place the tab's outcomes are mapped onto it. It was seven until
 * the conversation added three, and the count is written out in words at both
 * ends on purpose rather than derived: a vocabulary that stands short of what
 * its senders can send does not fail, it discards the word and keeps the row,
 * so the product goes quiet about a part of itself and nothing errors.
 * `MENTOR_CUES` did exactly that for months at nineteen words against
 * thirty-two cue kinds. Two numbers in prose on either side of a wire is a
 * cheap way to make that drift visible to somebody reading, and this pair has
 * now drifted once.
 *
 * `refused` is on that list and is now produced here, which it was not
 * before. It used to be invisible on this side of the wire — a model refusal
 * and a held-back reply were the same 200 with the same cue under them, so
 * there was nothing to tell them apart by and nothing to do with the answer.
 * There is no cue under either of them now, so the Worker names which one
 * happened and the learner reads the sentence that is true.
 *
 * `spent`, `capped` and `not-yours` were briefly folded into `error` here,
 * because the list had no word for any of them. It has all three now, and
 * they are worth the columns: `spent` running far ahead of `capped` says the
 * points bind and the cap is decoration, and `not-yours` is a number that
 * should be zero and is a stale or forged id if it is not. None of them is
 * `quota`, which in that table means an account's allowance and is what says
 * what a day's wait costs.
 *
 * `exhausted` is the eleventh word and the only one that measures money
 * rather than behaviour: a learner who has spent the whole of the course's
 * mentor budget and will get no more turns on this account, ever. It is what
 * says whether the budget is set anywhere near right, and it cannot be read
 * any other way round — the spend itself never leaves the Worker.
 *
 * `not-here` and `busy` are still the two with no word of their own. Both are
 * "the learner asked and got nothing", and inventing an eighth word here
 * would mean the Worker storing the row with no detail at all, which is worse
 * than a coarse bucket.
 */
export function askDetail(outcome: MentorAsk['outcome']): string {
  return outcome === 'not-here' || outcome === 'busy' ? 'error' : outcome;
}

/** At most this many characters of the learner's own words, as the Worker's own cap. */
export const MESSAGE_MAX = 500;

/* ── The conversation ────────────────────────────────────────────────────── */

/**
 * One thing that was said, as the rail draws it.
 *
 * `text` is on both sides because it is what goes on the wire: for the
 * learner it is what they typed, and for the model it is **what the learner
 * actually read** — the sentence, the question under it and the command shape
 * beside it, joined. Sending only `say` would drop the question the next
 * message is an answer to, which is the one thing a transcript is for.
 */
export type MentorTurn =
  | { role: 'learner'; text: string }
  | { role: 'mentor'; text: string; reply: MentorReply };

/** What crosses the wire: two fields, no ids, no timestamps, nothing about who. */
export interface MentorHistoryTurn {
  role: 'learner' | 'mentor';
  text: string;
}

/**
 * A conversation, which belongs to one level and one workspace.
 *
 * `id` is minted here, is new on every level change and every reset, and is
 * the only thing that ties two turns together anywhere in the product. There
 * is no row behind it: nothing is stored under this id on either side (see
 * "What is stored, and what is not" in docs/mentor.md), so it is a grouping
 * key for one tab's lifetime and not a handle on anything.
 */
export interface MentorConversation {
  id: string;
  /** Oldest first. Only exchanges that happened — a refused turn adds nothing. */
  turns: MentorTurn[];
  /**
   * What the Worker last said about the two limits, or null before it has
   * answered anything in this conversation.
   *
   * Null is a real state and not a missing value: the disclosure budget is
   * counted per account per level per rolling day, so a tab that has just
   * loaded genuinely does not know how far up the ladder this level has
   * already been taken today, and drawing a bar at zero would be a guess. It
   * fills in on the first answer.
   */
  spend: MentorSpend | null;
  /** Which limit closed it, once one has. Null while it can still be asked. */
  ended: 'spent' | 'capped' | null;
}

/**
 * The two limits, as the Worker counts them. Every answer and every refusal
 * that reached the conversation gate carries this block.
 *
 * **Both numbers are read, never declared.** They were constants here for
 * about a day, and the reason they are not is worth keeping: the budget is
 * `SUM(disclosure)` over `(user, mission, rolling day)` rather than over the
 * conversation, precisely because the tab mints the conversation id and
 * stores nothing — so a reload would have minted a new id and bought a fresh
 * budget. A number the tab cannot derive is a number the tab must be told,
 * and a limit declared on both sides of a wire is one rule in two places with
 * only one of the copies under test.
 *
 * What follows from the day key: `spent` does not start at zero when a
 * conversation does, and the bar is honestly "how far up the ladder this
 * level has been taken today" rather than "how much of this chat is left".
 * `messages`/`cap` is the one part that really is per conversation.
 *
 * `resetAt` is set only once the budget is gone, which is what makes it the
 * right thing to branch the ending sentence on: "come back tomorrow" and
 * "that is as far as this goes" are different things to do, and the number
 * says which is true without the panel having to know which path refused.
 */
export interface MentorSpend {
  id: string;
  /** Disclosure points this account has spent on this level today. */
  spent: number;
  budget: number;
  /** When the budget returns, or null while it has not run out. */
  resetAt: number | null;
  /** Messages in this conversation, which includes the ones that produced nothing. */
  messages: number;
  cap: number;
}

/**
 * Turns of history sent, newest kept.
 *
 * **Deliberately stricter than the Worker's backstop**, which re-trims to ten
 * messages and 2,000 tokens at four characters a token and clamps each one to
 * 2,000 characters. These three are inside all of that, so nothing here is
 * ever refused for length — and that is the point rather than a mismatch to
 * tidy up. The history is billed as fresh input on every message, only this
 * end knows what was actually on the screen, and the cheaper of two trims is
 * the one that should bind. Loosening these to "match" would spend money to
 * send the Worker something it is about to throw away.
 *
 * The two character ceilings were doubled when the mentor could first see the
 * work. Its replies now quote the learner's code back at them, and a history
 * that kept 600 characters of a reply kept the sentence and cut the line of
 * code it was about — which is the half the next question is asking after.
 */
const HISTORY_TURNS = 8;

/** The character ceiling on all of the history together. Under the Worker's 2,000 tokens. */
const HISTORY_CHARS = 4800;

/** The ceiling on any one turn inside the history. Under the Worker's 2,000 characters. */
const TURN_CHARS = 1200;

/**
 * A new conversation id.
 *
 * `crypto.randomUUID` where there is one, and a v4 built by hand where there
 * is not — an insecure context, an old browser, a test environment — because
 * the alternative is a turn that cannot be posted at all. It is a grouping
 * key, so what it must be is *distinct*; the uuid shape is for the Worker's
 * validator rather than for anything here.
 */
export function newConversationId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** An empty conversation with a fresh id. The state a level opens in, and the state a reset returns to. */
export function newConversation(): MentorConversation {
  return { id: newConversationId(), turns: [], spend: null, ended: null };
}

/**
 * What the learner read, as one string, for the history.
 *
 * The three parts in the order they are on the screen. `look` goes in as
 * well, even though it is machine text: "that printed nothing" is a perfectly
 * ordinary next message and it is about the command, so a history without it
 * is a history the next turn cannot follow.
 */
export function saidText(reply: MentorReply): string {
  return [reply.say, reply.ask, reply.look].filter((part): part is string => !!part).join('\n');
}

/**
 * The history to post: oldest first, trimmed to fit.
 *
 * Every turn resends this, so its cost grows with the conversation and the
 * trim is the whole reason this function exists. Whole turns are dropped from
 * the front rather than sentences from the middle, because half an exchange
 * reads as a model that forgot what it just said.
 *
 * The Worker re-trims on arrival. That is a defence against a tab somebody
 * opened the console on, not a reason to send more: the bill is the same
 * whichever end cuts it, and only this end knows what was on the screen.
 */
export function historyFor(turns: readonly MentorTurn[]): MentorHistoryTurn[] {
  const kept: MentorHistoryTurn[] = turns
    .slice(-HISTORY_TURNS)
    .map((turn) => ({ role: turn.role, text: turn.text.slice(0, TURN_CHARS) }));
  let total = kept.reduce((sum, turn) => sum + turn.text.length, 0);
  while (kept.length > 1 && total > HISTORY_CHARS) {
    total -= kept[0]!.text.length;
    kept.shift();
  }
  return kept;
}

/**
 * How far through this is, and whether it is nearly over.
 *
 * `fraction` is a fraction rather than a count, and that is a design decision
 * rather than a convenience — see `AskMentor.tsx`. It takes **whichever of
 * the two limits is further along**, because the one that runs out first is
 * the one that ends the conversation, and a readout tracking the slacker of
 * the two would sit half full at the moment the panel closed.
 *
 * Nothing here is arithmetic of the tab's own any more. It was, and the sum
 * over the transcript was wrong in a way nobody would have noticed on screen:
 * it restarted at zero on a reload while the day's spend carried on, so the
 * bar would have read empty on a level that had one point left in it.
 *
 * `atLimit` is checked in the Worker's own order — the message cap before the
 * budget — so that when both are gone the two halves name the same one.
 */
export function conversationState(spend: MentorSpend | null): {
  fraction: number;
  nearEnd: boolean;
  atLimit: 'spent' | 'capped' | null;
} {
  if (!spend) return { fraction: 0, nearEnd: false, atLimit: null };
  const points = spend.budget > 0 ? spend.spent / spend.budget : 0;
  const messages = spend.cap > 0 ? spend.messages / spend.cap : 0;
  return {
    fraction: Math.min(1, Math.max(0, points, messages)),
    // One more message, or less than one full-strength answer's worth of
    // room. Either is a conversation with one turn left in it at most, which
    // is the moment the learner should be told rather than the moment after.
    nearEnd: spend.budget - spend.spent <= 4 || spend.cap - spend.messages <= 1,
    atLimit: spend.messages >= spend.cap ? 'capped' : spend.spent >= spend.budget ? 'spent' : null,
  };
}

export interface MentorAskRequest {
  missionId: string;
  lang: 'en' | 'ar' | 'de' | 'ja';
  context: MentorContext;
  /** The learner's own words, already trimmed to `MESSAGE_MAX`. */
  message?: string;
  choice?: { situation: string; index: number };
  /** This conversation, minted in the tab. One per level, and one per reset. */
  conversationId: string;
  /** What has been said so far, oldest first, already trimmed by `historyFor`. */
  history: readonly MentorHistoryTurn[];
  /**
   * The seed values this learner has not been shown (`api.mentorHidden`),
   * which the Worker's output filter holds back even when the terminal shows
   * one. Beside `context` and never in it: the context is what the model is
   * told, and a list of what not to say is a list of the answers.
   */
  hidden?: readonly string[];
}

function readWindow(value: unknown): MentorWindow {
  const w = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  return {
    used: typeof w.used === 'number' ? w.used : 0,
    limit: typeof w.limit === 'number' ? w.limit : 0,
    resetAt: typeof w.resetAt === 'number' ? w.resetAt : null,
  };
}

/**
 * The allowance, or null.
 *
 * Defensive on every field for the reason `parsePull` is: this is drawn in a
 * sentence with two numbers in it, and a panel that says "undefined of
 * undefined questions today" is worse than a panel that says nothing. Null
 * when the body carried none, which is a real answer — a 403 has no allowance
 * to report because nothing was spent.
 */
function readAllowance(value: unknown): MentorAllowance | null {
  if (!value || typeof value !== 'object') return null;
  const a = value as Record<string, unknown>;
  return {
    level: readWindow(a.level),
    day: readWindow(a.day),
    month: readWindow(a.month),
    deploymentPaused: a.deploymentPaused === true,
    budgetSpent: a.budgetSpent === true,
    nextEligibleAt: typeof a.nextEligibleAt === 'number' ? a.nextEligibleAt : null,
  };
}

function readReply(value: unknown): MentorReply | null {
  if (!value || typeof value !== 'object') return null;
  const r = value as Record<string, unknown>;
  if (typeof r.say !== 'string' || r.say.trim() === '') return null;
  const disclosure = typeof r.disclosure === 'number' && r.disclosure >= 1 && r.disclosure <= 4
    ? (Math.round(r.disclosure) as 1 | 2 | 3 | 4)
    : 4;
  const reply: MentorReply = { say: r.say, disclosure };
  if (typeof r.look === 'string' && r.look.trim() !== '') reply.look = r.look;
  if (typeof r.ask === 'string' && r.ask.trim() !== '') reply.ask = r.ask;
  if (typeof r.situation === 'string' && r.situation.trim() !== '') reply.situation = r.situation;
  return reply;
}

function readTier(value: unknown): MentorTier {
  return value === 'paid' ? 'paid' : 'free';
}

/**
 * The two limits, or null when this answer did not carry them.
 *
 * Defensive on every field for `readAllowance`'s reason, with one of its own:
 * these two numbers are a bar somebody looks at to decide whether to ask
 * another question, and a bar drawn from `NaN` is a bar that says something
 * false rather than nothing. A body without the block — a 401, a 409, the
 * 403s, the plain `quota` 429 — is null, which the panel draws as no bar at
 * all.
 */
function readSpend(value: unknown): MentorSpend | null {
  if (!value || typeof value !== 'object') return null;
  const c = value as Record<string, unknown>;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.max(0, v) : 0);
  if (typeof c.id !== 'string') return null;
  return {
    id: c.id,
    spent: num(c.spent),
    budget: num(c.budget),
    resetAt: typeof c.resetAt === 'number' ? c.resetAt : null,
    messages: num(c.messages),
    cap: num(c.cap),
  };
}

/**
 * One turn.
 *
 * Never rejects, including on abort: a cancelled turn comes back as `error`,
 * and the panel says nothing came back, which is true of somebody who
 * navigated away and came back. The only thing a caller has to remember is
 * that the outcome is a word, not a status.
 */
export async function askMentorModel(request: MentorAskRequest, signal?: AbortSignal): Promise<MentorAsk> {
  const body: Record<string, unknown> = {
    missionId: request.missionId,
    lang: request.lang,
    context: request.context,
    conversationId: request.conversationId,
    history: request.history,
  };
  if (request.message) body.message = request.message.slice(0, MESSAGE_MAX);
  if (request.choice) body.choice = request.choice;
  // Only the strings, and only when there are any. The Worker clamps whatever
  // arrives, so this is not the defence; it is what keeps a malformed list
  // from ever being the reason this function had something to throw.
  const hidden = Array.isArray(request.hidden) ? request.hidden.filter((v) => typeof v === 'string') : [];
  if (hidden.length > 0) body.hidden = hidden;

  let response: Response;
  let value: unknown;
  try {
    response = await fetch('/api/mentor/ask', {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    value = await response.json();
  } catch {
    // The network, an abort, or a body that is not JSON. One answer, and the
    // only one in this file that cannot name what went wrong, because nothing
    // came back to name it with.
    return { outcome: 'error' };
  }

  const payload = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const allowance = readAllowance(payload.allowance);
  const tier = readTier(payload.tier);
  const conversation = readSpend(payload.conversation);
  const error = typeof payload.error === 'string' ? payload.error : '';

  if (response.status === 401) return { outcome: 'sign-in' };
  // Two 403s and they are not the same answer. `not-here` is about the
  // deployment or the level and there is nothing to do about it; `not-yours`
  // is about one identifier, and the thing to do about it is carry on in a
  // new conversation. A status code alone cannot tell them apart, which is
  // why the word is read here rather than inferred.
  if (response.status === 403) {
    return error === 'not-yours'
      ? { outcome: 'not-yours' }
      : { outcome: 'not-here', tier: readTierOrNull(payload.tier) };
  }
  if (response.status === 409) return { outcome: 'busy' };
  if (response.status === 429) {
    // Four refusals share this status and only the word says which. `quota`
    // is the account's turn allowance, `exhausted` the course's money budget
    // and the one that never returns; the other two are the conversation's
    // own limits and carry the block that says how far along they are.
    // Unknown reads as `quota`, which is what a 429 has always meant here.
    if (error === 'spent' || error === 'capped') return { outcome: error, allowance, tier: readTierOrNull(payload.tier), conversation };
    // The course's whole mentor budget, gone. Read by its word rather than
    // inferred from a null `nextEligibleAt`, because "no reset time" is also
    // what a paused deployment looks like and they are opposite sentences.
    if (error === 'exhausted') return { outcome: 'exhausted', allowance };
    return { outcome: 'quota', allowance };
  }
  if (!response.ok) return { outcome: 'error' };

  const reply = readReply(payload.reply);
  if (reply) return { outcome: payload.cached === true ? 'cached' : 'answered', reply, allowance, tier, conversation };
  // A 200 with no reply, which the Worker now explains rather than papering
  // over with the cue it used to echo. The default is `error` and not
  // `filtered`: an unexplained empty answer is "nothing came back", and
  // reading it as a held-back reply would tell the learner something about
  // their own question that nobody established.
  const named = REASONS[typeof payload.reason === 'string' ? payload.reason : ''];
  if (named) return { outcome: named, allowance, tier, conversation };
  return { outcome: 'error' };
}

/**
 * The tier, or null where the body did not say.
 *
 * Only the conversation's own 429 is in that position: it refuses before a
 * turn is reserved and carries no tier. `readTier` answers `free` for a
 * missing field, which is right everywhere it is used and wrong here — it
 * would tell a learner who has paid for the course about the free tier's
 * allowance, on the one screen where they have just been refused something.
 */
function readTierOrNull(value: unknown): MentorTier | null {
  return value === 'paid' || value === 'free' ? value : null;
}

/**
 * The words a 200 with no reply may carry, and what each one means here.
 *
 * A table rather than a chain of comparisons because it is now the seam
 * between two halves written by two people: the Worker names the reason and
 * this end has one sentence per name. **An unrecognised word is not a guess.**
 * It falls through to `error`, which says "nothing came back" — the one
 * sentence that is true of every unexplained empty answer — rather than
 * telling a learner something about their own question that nobody
 * established.
 *
 * Three of the four are the whole of what a 200 can say. `capped` is here
 * because it is a word this end has a sentence for and a table that accepted
 * three of a family of four would be a trap for whoever adds the fourth path;
 * it arrives as a 429 today.
 *
 * It briefly held aliases, while the two ends were being written in parallel
 * and a near-miss on a word would have cost a learner the honest sentence.
 * The Worker has landed and the words are settled, so the aliases are gone:
 * a table that quietly accepts three spellings is a table nobody can read the
 * contract off.
 */
const REASONS: Record<string, 'filtered' | 'refused' | 'spent' | 'capped'> = {
  filtered: 'filtered',
  refused: 'refused',
  spent: 'spent',
  capped: 'capped',
};

/*
 * `rememberReply` stood here, and it is gone with the field it fed.
 *
 * It kept the last three of the model's own sentences so the prompt could ask
 * it not to repeat itself — half a conversation, unlabelled as to who said
 * what, and the best that could be done while a turn was the unit. The
 * transcript is strictly better and keeping both would be two memories of one
 * conversation that have to agree. `MentorContextInput.lastReplies` and
 * `MAX_REPLIES` in the engine are deprecated and ignored; nothing here passes
 * them any more.
 */
